/**
 * Reject Job Dialog
 * Confirmation dialog for rejecting a job in pending_review back to in_progress.
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { Copy, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRejectInstallationJob } from '../hooks/useInstallationJobs';
import type { InstallationJob } from '../types';

interface RejectJobDialogProps {
  job: InstallationJob;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RejectJobDialog({ job, open, onOpenChange }: RejectJobDialogProps) {
  const rejectJob = useRejectInstallationJob();
  const [reason, setReason] = useState('');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [emailFailure, setEmailFailure] = useState<{ accessCode?: string; error?: string } | null>(null);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setReason('');
      setNotifyEmail('');
      setEmailFailure(null);
    }
    onOpenChange(isOpen);
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      const result = await rejectJob.mutateAsync({
        id: job.id,
        input: {
          reason: reason.trim(),
          notifyEmail: notifyEmail.trim() || undefined,
        },
      });

      if (result.rejectionNotification?.requested && !result.rejectionNotification.sent) {
        setEmailFailure({
          accessCode: result.rejectionNotification.accessCode,
          error: result.rejectionNotification.error,
        });
        toast.error('Job rejected, but the email could not be sent');
        return;
      }

      toast.success(
        result.rejectionNotification?.sent
          ? 'Job rejected and new access code emailed'
          : 'Job rejected and returned to technician',
      );
      setReason('');
      setNotifyEmail('');
      onOpenChange(false);
    } catch {
      toast.error('Failed to reject job');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            Reject & Return to Technician
          </DialogTitle>
          <DialogDescription>
            Reject <strong>{job.jobNumber}</strong> — {job.title}. This will return the job
            to the technician while preserving completed field data for the required corrections.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Rejection Reason <span className="text-red-500">*</span></Label>
            <textarea
              id="reject-reason"
              placeholder="Explain what needs to be corrected..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notify-email">Notify Technician by Email (optional)</Label>
            <Input
              id="notify-email"
              type="email"
              placeholder="technician@example.com"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
            />
          </div>

          {emailFailure && (
            <div role="alert" className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
              <p className="font-medium">The job was rejected, but the email could not be sent.</p>
              {emailFailure.accessCode ? (
                <div className="flex items-center justify-between gap-3 rounded border border-amber-200 bg-white p-2">
                  <div>
                    <p className="text-xs text-muted-foreground">New access code</p>
                    <code className="font-mono text-base font-bold">{emailFailure.accessCode}</code>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(emailFailure.accessCode!);
                      toast.success('Access code copied');
                    }}
                  >
                    <Copy className="mr-1 h-4 w-4" />
                    Copy
                  </Button>
                </div>
              ) : (
                <p>Generate a new code from the Access Codes tab before contacting the technician.</p>
              )}
              {emailFailure.error && <p className="text-xs">{emailFailure.error}</p>}
            </div>
          )}
        </div>

        <DialogFooter>
          {emailFailure ? (
            <Button onClick={() => handleOpenChange(false)}>Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={rejectJob.isPending || !reason.trim()}
              >
                {rejectJob.isPending ? 'Rejecting...' : 'Reject Job'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
